import * as z from "zod";

// Compact order summary the detail page renders for the matched order (and for
// each potential match when the alert was ambiguous).
const disputeAlertOrderSummary = z.object({
  orderId: z.string(),
  // Shopify's numeric order id (from the synced order's `shopify.id`), used to
  // build the "Open in Shopify" admin link. Absent if the order isn't synced.
  shopifyOrderId: z.string().optional(),
  orderName: z.string(),
  customerName: z.string().optional(),
  customerEmail: z.string().optional(),
  createdAt: z.string().optional(),
  totalPrice: z.string().optional(),
  currency: z.string().optional(),
  itemCount: z.number(),
});

export type DisputeAlertOrderSummary = z.infer<typeof disputeAlertOrderSummary>;

// Matches an alert to a Shopify order (the server runs the same matcher the
// automation engine uses) and returns a compact order summary for the detail
// page. "no_match" drives an explanatory note; "multiple_matches" returns the
// ambiguous orders as `candidates` so the merchant can disambiguate.
export const getDisputelyAlertOrder = {
  name: "getDisputelyAlertOrder" as const,
  input: z.object({ alertId: z.coerce.bigint() }),
  // Discriminated union mirroring the domain `AlertOrderMatch`: each outcome
  // carries exactly its own fields, so illegal states (no_match with candidates,
  // matched with no currency) are unrepresentable and the client narrows on
  // `outcome` instead of optional-chaining every field.
  output: z.discriminatedUnion("outcome", [
    z.object({
      outcome: z.literal("matched"),
      // The matched order's (presentment) currency.
      currency: z.string(),
      // Absent only when the matched Order doc couldn't be loaded.
      order: disputeAlertOrderSummary.optional(),
    }),
    z.object({ outcome: z.literal("no_match") }),
    z.object({
      outcome: z.literal("multiple_matches"),
      // The orders that ambiguously matched, for the merchant to disambiguate.
      candidates: z.array(disputeAlertOrderSummary),
    }),
  ]),
};
